import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  acceptOffer,
  applyToShift,
  cancelShift,
  confirmShift,
  declineOffer,
  getWorkerAttention,
  getWorkerEvents,
  getWorkerNearby,
  getWorkerUpcoming,
  markWorkerEventsRead,
  reportRunningLate,
  withdrawFromShift,
} from "@/queries/api/workerDashboardApi";
import type {
  CancelReason,
  NearbyParams,
  WorkerAttentionItem,
  WorkerEvent,
  WorkerEventFilter,
  WorkerNearbyResult,
  WorkerUpcomingResult,
} from "@/queries/api/workerDashboardApi";

import { QueryKeys } from "../queryKeys";

type WorkerEventsData = { unread: number; items: Array<WorkerEvent> };

const UPCOMING_KEY = [QueryKeys.UPCOMING_SHIFTS, "worker-v2"] as const;

export function useWorkerAttentionQuery(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery<Array<WorkerAttentionItem>>({
    queryKey: [QueryKeys.WORKER_ATTENTION],
    queryFn: getWorkerAttention,
    staleTime: 30_000,
    ...options,
  });
}

export function useWorkerUpcomingQuery(limit = 3) {
  return useQuery<WorkerUpcomingResult>({
    queryKey: [...UPCOMING_KEY, limit],
    queryFn: () => getWorkerUpcoming(limit),
    staleTime: 30_000,
  });
}

/** Withdraw an applied shift (SPEC §3.2 / §8.2). Refreshes upcoming + attention on settle. */
export function useWithdrawShiftMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: number | string) => withdrawFromShift(shiftId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UPCOMING_SHIFTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_ATTENTION] });
    },
  });
}

/** Cancel an upcoming shift (SPEC §3.2). Refreshes upcoming + attention on settle. */
export function useCancelShiftMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shiftId,
      reason,
      note,
    }: {
      shiftId: number | string;
      reason: CancelReason;
      note?: string;
    }) => cancelShift(shiftId, { reason, note }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UPCOMING_SHIFTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_ATTENTION] });
    },
  });
}

/** Report running late for an upcoming shift (SPEC §3.2). Refreshes upcoming + attention. */
export function useRunningLateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      shiftId,
      etaMinutes,
      note,
    }: {
      shiftId: number | string;
      etaMinutes?: number;
      note?: string;
    }) => reportRunningLate(shiftId, { etaMinutes, note }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UPCOMING_SHIFTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_ATTENTION] });
    },
  });
}

export function useWorkerNearbyQuery(params: NearbyParams) {
  return useQuery<WorkerNearbyResult>({
    queryKey: [QueryKeys.WORKER_NEARBY, params],
    queryFn: () => getWorkerNearby(params),
    staleTime: 30_000,
  });
}

/** Cursor-paginated nearby shifts for the full "See all shifts" page. */
export function useInfiniteWorkerNearbyQuery(params: Omit<NearbyParams, "limit" | "cursor">) {
  return useInfiniteQuery({
    queryKey: [QueryKeys.WORKER_NEARBY, "infinite", params],
    queryFn: ({ pageParam }) => getWorkerNearby({ ...params, limit: 20, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useWorkerEventsQuery(filter: WorkerEventFilter = "all") {
  return useQuery<WorkerEventsData>({
    queryKey: [QueryKeys.EVENTS, "worker-v2", filter],
    queryFn: () => getWorkerEvents(filter),
    staleTime: 30_000,
  });
}

/** Mark worker activity read; zeroes the unread pill in cache (SPEC §3.4). */
export function useMarkWorkerEventsReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markWorkerEventsRead,
    onSuccess: () => {
      queryClient.setQueriesData<WorkerEventsData>(
        { queryKey: [QueryKeys.EVENTS, "worker-v2"] },
        (old) => (old ? { ...old, unread: 0 } : old),
      );
    },
  });
}

function invalidateAfterOfferResponse(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_ATTENTION] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.UPCOMING_SHIFTS] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_NEARBY] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.EVENTS, "worker-v2"] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.SCHEDULE] });
}

/** Accept a shift an employer offered. Refreshes attention/upcoming/activity on settle. */
export function useAcceptOfferMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: number | string) => acceptOffer(shiftId),
    onSettled: () => invalidateAfterOfferResponse(queryClient),
  });
}

/** Decline a shift an employer offered. Refreshes attention/upcoming/activity on settle. */
export function useDeclineOfferMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: number | string) => declineOffer(shiftId),
    onSettled: () => invalidateAfterOfferResponse(queryClient),
  });
}

/** Apply to a nearby shift (SPEC §3.3). Refreshes nearby + upcoming on settle. */
export function useApplyToShiftMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: number | string) => applyToShift(shiftId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_NEARBY] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UPCOMING_SHIFTS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.WORKER_ATTENTION] });
    },
  });
}

/**
 * Confirm a shift. Optimistically drops the matching confirm_shift item from the
 * For-you strip (SPEC §8.1 — updates without reload), then refreshes attention and
 * the worker's upcoming shifts from the server.
 */
export function useConfirmShiftMutation() {
  const queryClient = useQueryClient();
  const key = [QueryKeys.WORKER_ATTENTION];

  return useMutation({
    mutationFn: (shiftId: number | string) => confirmShift(shiftId),
    onMutate: async (shiftId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Array<WorkerAttentionItem>>(key);
      queryClient.setQueryData<Array<WorkerAttentionItem>>(key, (old) =>
        (old ?? []).filter(
          (item) =>
            !(item.type === "confirm_shift" && String(item.shift_id) === String(shiftId)),
        ),
      );
      return { previous };
    },
    onError: (_err, _shiftId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UPCOMING_SHIFTS] });
    },
  });
}
